import {useCallback, useEffect, useMemo} from "react";
import {generatePath, useMatch, useNavigate} from "react-router-dom";

import {useAppDispatch, useAppSelector} from "~/modules/redux";
import {UserActions} from "~/modules/redux/reducers/user";

import {useAllStores} from "./useAllStores";

const STORE_SESSION_KEY = "selectedStoreId";
const STORE_ROUTE_PATTERN = "/store/:storeId/*";

export const useSelectedStore = () => {
  const navigate = useNavigate();
  const match = useMatch(STORE_ROUTE_PATTERN);

  const dispatch = useAppDispatch();
  const storeId = useAppSelector((state) => state.user.selectedStoreId);

  const {stores} = useAllStores();

  const routeStoreId = match?.params.storeId ?? null;

  const validStoreIds = useMemo(() => new Set(stores.map((store) => store.id)), [stores]);

  // Priority: route param > Redux (hydrated from sessionStorage) > first store
  const selectedStoreId = (() => {
    if (routeStoreId && validStoreIds.has(routeStoreId)) return routeStoreId;
    if (storeId && validStoreIds.has(storeId)) return storeId;
    return stores[0]?.id ?? null;
  })();

  const setSelectedStoreId = useCallback(
    (newStoreId: string) => {
      if (!validStoreIds.has(newStoreId)) return;

      dispatch(UserActions.setSelectedStoreId(newStoreId));

      // Keep only the section root (e.g. "/store/XXX/banner" instead of "/store/XXX/banner/NO/promo-banner/123/edit")
      if (match) {
        const sectionRoot = (match.params["*"] ?? "").split("/")[0];
        navigate(generatePath(STORE_ROUTE_PATTERN, {storeId: newStoreId, "*": sectionRoot || null}));
      }
    },
    [validStoreIds, dispatch, match, navigate],
  );

  // Sync route param to Redux when navigating to a store page via direct link or back/forward
  useEffect(() => {
    if (!routeStoreId) return;
    if (!validStoreIds.has(routeStoreId)) return;
    if (routeStoreId === storeId) return;

    dispatch(UserActions.setSelectedStoreId(routeStoreId));
  }, [routeStoreId, validStoreIds, storeId, dispatch]);

  // Persist resolved storeId to sessionStorage for refresh survival
  useEffect(() => {
    if (!selectedStoreId) return;
    sessionStorage.setItem(STORE_SESSION_KEY, selectedStoreId);
  }, [selectedStoreId]);

  return [selectedStoreId, setSelectedStoreId] as const;
};

export const useStorePath = () => {
  const [storeId] = useSelectedStore();

  return useCallback(
    (pattern: string, params?: Record<string, string>) => generatePath(pattern, {storeId: storeId || "-", ...params}),
    [storeId],
  );
};
